import { ApiMethod } from '@wajeez-web/stores/base';
import { Server, contentClient } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { PaginatedRequest } from '@wajeez-web/core/api';
import { thunkErrorHandler } from '@wajeez-web/core/utils';

export const getAllAuthors: ApiMethod<
  PaginatedRequest,
  Server.AuthorOutput[],
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_get_V1(undefined, data.page, data.pageSize);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getAuthorByIdMultilingual: ApiMethod<
  number,
  Server.AuthorMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Authors_Client(undefined, contentClient);

      const response = await client.admin_Authors_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const createNewAuthor: ApiMethod<
  Server.CreateAuthorInput,
  number,
  any
> = async (variables, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_post_V1(variables);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const createAuthorMultilingual: ApiMethod<
  Server.CreateAuthorV2Input,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Authors_Client(undefined, contentClient);

      const response = await client.admin_Authors_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editAuthor: ApiMethod<
  Server.EditAuthorInput,
  number,
  any
> = async (variables, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_put_V1(variables);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const editAuthorMultilingual: ApiMethod<
  Server.EditAuthorV2Input,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Authors_Client(undefined, contentClient);

      const response = await client.admin_Authors_put_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const deleteAuthor: ApiMethod<number, void, any> = async (
  id,
  { dispatch, rejectWithValue }
) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_delete_V1(id);
    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const assignAuthortoContent: ApiMethod<
  Server.AssignAuthorToContentEntityInput,
  void,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_assign_post_V1(data);
    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntityAuthors: ApiMethod<
  PaginatedRequest & { ContentEntityId: string },
  Server.AuthorOutput[],
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_byContentEntity_get_V1(
      undefined,
      data.ContentEntityId,
      data.page,
      data.pageSize
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getAuthorById: ApiMethod<
  number,
  Server.AuthorOutput,
  any
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const addAuthorByIdForHistory: ApiMethod<
  number,
  Server.AuthorOutput,
  any
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getAuthorsAsDataTable: ApiMethod<
  Server.AuthorDatatable,
  Server.TableOfAuthorColumns,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_datatable_post_V1(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getAuthorsToBeExported: ApiMethod<
  Server.AuthorDatatable,
  Server.TableOfAuthorColumns,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_datatable_post_V1(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};
export const getAuthorsLookUps: ApiMethod<
  string,
  Server.AuthorLookUpOutput[],
  any
> = async (SearchString, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_lookups_get_V1(undefined, SearchString);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const unAssignAuthortoContent: ApiMethod<
  Server.AssignAuthorToContentEntityInput,
  void,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Authors_Client(undefined, contentClient);
    const response = await client.authors_unAssign_post_V1(data);
    if (response.success) {
      return;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};
